import { IReportColumnDef } from '../../../shared/shared-models/reports/report-column.interface';
import { ISelectOption } from '../../../shared/shared-models/custom-select/custom-select';
import { ReportValueType } from '../../../shared/shared-models/reports/report-value-type.enum';
import { IStockTakeFinalFigure, IStockTakeItemDetail, StockTakeItemStatus } from '../models';
import {
  STOCK_TAKE_SUMMARY_REPORT_COLUMNS,
  StockTakeReportColumnId,
} from '../models/stock-take-report-column';

/**
 * Default row sort for stock take summary PDFs and confirm-process review lists.
 */
export const STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID: StockTakeReportColumnId =
  StockTakeReportColumnId.variance;

/**
 * Confirm-process modal only shows rand variance — keep list order aligned with that column.
 */
export const STOCK_TAKE_CONFIRM_PROCESS_LIST_SORT_COLUMN_ID: StockTakeReportColumnId =
  StockTakeReportColumnId.randVariance;

/**
 * Build single-select options for summary report sort column (printable columns only).
 */
export function buildStockTakeReportSortColumnOptions(
  includeDelCol: boolean,
  selectedColumnId: string = STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID,
): Record<string, ISelectOption> {
  const printableIds = getSummaryPrintColumnIdsForSort(includeDelCol);
  const options: Record<string, ISelectOption> = {};
  for (const columnId of printableIds) {
    const column = STOCK_TAKE_SUMMARY_REPORT_COLUMNS.find((def) => def.id === columnId);
    if (!column) {
      continue;
    }
    options[columnId] = {
      name: column.label,
      disabled: false,
      selected: columnId === selectedColumnId,
    };
  }
  return options;
}

/**
 * Sort finalised snapshot rows for PDF output (does not mutate the input array).
 */
export function sortStockTakeFinalFiguresForReport(
  figures: IStockTakeFinalFigure[],
  sortColumnId: string = STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID,
): IStockTakeFinalFigure[] {
  return [...figures].sort((left: IStockTakeFinalFigure, right: IStockTakeFinalFigure) =>
    compareStockTakeReportRowsDescending(
      left.code,
      readFinalFigureSortValue(left, sortColumnId),
      right.code,
      readFinalFigureSortValue(right, sortColumnId),
      sortColumnId,
      resolveReportValueType(sortColumnId),
    ),
  );
}

/**
 * Sort counted/unique item details for on-screen review (same order as the PDF).
 */
export function sortStockTakeItemDetailsForReport(
  items: IStockTakeItemDetail[],
  sortColumnId: string = STOCK_TAKE_REPORT_DEFAULT_SORT_COLUMN_ID,
): IStockTakeItemDetail[] {
  const reportItems = items.filter(
    (item: IStockTakeItemDetail) =>
      item.status === StockTakeItemStatus.counted || item.status === StockTakeItemStatus.unique,
  );
  return [...reportItems].sort((left: IStockTakeItemDetail, right: IStockTakeItemDetail) =>
    compareStockTakeReportRowsDescending(
      left.code,
      readItemDetailSortValue(left, sortColumnId),
      right.code,
      readItemDetailSortValue(right, sortColumnId),
      sortColumnId,
      resolveReportValueType(sortColumnId),
    ),
  );
}

/**
 * * Comparator for Array.sort — largest discrepancies first for variance columns; otherwise highest value first. */
function compareStockTakeReportRowsDescending(
  leftCode: string,
  leftValue: string | number,
  rightCode: string,
  rightValue: string | number,
  sortColumnId: string,
  valueType: ReportValueType,
): number {
  if (isNumericReportValueType(valueType)) {
    const leftNumber = toSortableNumber(leftValue);
    const rightNumber = toSortableNumber(rightValue);
    const numericCompare = compareReportNumericValues(leftNumber, rightNumber, sortColumnId);
    if (numericCompare !== 0) {
      return numericCompare;
    }
  } else {
    const textCompare = String(rightValue ?? '').localeCompare(String(leftValue ?? ''), undefined, {
      sensitivity: 'base',
    });
    if (textCompare !== 0) {
      return textCompare;
    }
  }
  return leftCode.localeCompare(rightCode, undefined, { sensitivity: 'base' });
}

function toSortableNumber(value: string | number): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

/**
 * Variance and rand variance: largest magnitude first (e.g. -2193 before -731 before 0).
 * Other numeric columns: highest value first.
 */
function compareReportNumericValues(left: number, right: number, sortColumnId: string): number {
  const useMagnitude =
    sortColumnId === StockTakeReportColumnId.variance
    || sortColumnId === StockTakeReportColumnId.randVariance;
  if (useMagnitude) {
    const leftMagnitude = Math.abs(left);
    const rightMagnitude = Math.abs(right);
    if (leftMagnitude > rightMagnitude) {
      return -1;
    }
    if (leftMagnitude < rightMagnitude) {
      return 1;
    }
  }
  if (left > right) {
    return -1;
  }
  if (left < right) {
    return 1;
  }
  return 0;
}

function isNumericReportValueType(valueType: ReportValueType): boolean {
  return (
    valueType === ReportValueType.number
    || valueType === ReportValueType.quantity
    || valueType === ReportValueType.currency
    || valueType === ReportValueType.date
    || valueType === ReportValueType.dateTime
  );
}

function resolveReportValueType(columnId: string): ReportValueType {
  const column = STOCK_TAKE_SUMMARY_REPORT_COLUMNS.find((def) => def.id === columnId);
  return column?.valueType ?? ReportValueType.string;
}

function getSummaryPrintColumnIdsForSort(includeDelCol: boolean): string[] {
  return STOCK_TAKE_SUMMARY_REPORT_COLUMNS
    .filter((column: IReportColumnDef) => {
      if (column.id === StockTakeReportColumnId.latestCost) {
        return false;
      }
      if (!includeDelCol) {
        return (
          column.id !== StockTakeReportColumnId.delColQty
          && column.id !== StockTakeReportColumnId.delColMovement
        );
      }
      return true;
    })
    .map((column: IReportColumnDef) => column.id);
}

function readFinalFigureSortValue(
  figure: IStockTakeFinalFigure,
  columnId: string,
): string | number {
  switch (columnId as StockTakeReportColumnId) {
    case StockTakeReportColumnId.code:
      return figure.code;
    case StockTakeReportColumnId.description:
      return figure.description ?? '';
    case StockTakeReportColumnId.qtyCounted:
      return figure.qtyCounted ?? 0;
    case StockTakeReportColumnId.timesCounted:
      return figure.timesCounted ?? 0;
    case StockTakeReportColumnId.binLocation:
      return figure.binLocation ?? '';
    case StockTakeReportColumnId.delColQty:
      return figure.delColQty ?? 0;
    case StockTakeReportColumnId.onhand:
      return figure.onhand ?? 0;
    case StockTakeReportColumnId.movement:
      return figure.movement ?? 0;
    case StockTakeReportColumnId.delColMovement:
      return figure.delColMovement ?? 0;
    case StockTakeReportColumnId.variance:
      return figure.variance ?? figure.varianceQty ?? 0;
    case StockTakeReportColumnId.latestCost:
      return figure.latestCost ?? 0;
    case StockTakeReportColumnId.avCost:
      return figure.avCost ?? 0;
    case StockTakeReportColumnId.randVariance:
      return figure.randVariance ?? 0;
    case StockTakeReportColumnId.countedByEmail:
      return figure.countedByEmail ?? '';
    default:
      return '';
  }
}

function readItemDetailSortValue(item: IStockTakeItemDetail, columnId: string): string | number {
  switch (columnId as StockTakeReportColumnId) {
    case StockTakeReportColumnId.code:
      return item.code;
    case StockTakeReportColumnId.description:
      return item.description ?? '';
    case StockTakeReportColumnId.qtyCounted:
      return item.qtyCounted ?? 0;
    case StockTakeReportColumnId.timesCounted:
      return item.timesCounted ?? 0;
    case StockTakeReportColumnId.binLocation:
      return item.binLocation ?? '';
    case StockTakeReportColumnId.delColQty:
      return item.delColQty ?? 0;
    case StockTakeReportColumnId.onhand:
      return item.onhand ?? 0;
    case StockTakeReportColumnId.movement:
      return item.movement ?? 0;
    case StockTakeReportColumnId.delColMovement:
      return item.delColMovement ?? 0;
    case StockTakeReportColumnId.variance:
      return item.variance ?? 0;
    case StockTakeReportColumnId.latestCost:
      return item.latestCost ?? 0;
    case StockTakeReportColumnId.avCost:
      return item.avCost ?? 0;
    case StockTakeReportColumnId.randVariance:
      return item.randVariance ?? 0;
    case StockTakeReportColumnId.countedByEmail:
      return item.countedByEmail ?? '';
    default:
      return '';
  }
}
